import { Editor, Transforms, Range, Text } from 'slate';
import { jsx } from 'slate-hyperscript';
import {
  get,
  isString,
  isObject,
  isArray,
  isNil,
  flatten,
  replace,
  startsWith,
  isUndefined,
} from 'lodash';

import { clearWhitespace } from 'utils/textHelpers';
import {
  QUESTION_DESCRIPTION,
  QUESTION_VAN_WESTENDORP,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_MATRIX_CONSTANT_SUM,
  QUESTION_RATING,
  QUESTION_SLIDER,
  QUESTION_DRILLDOWN,
  QUESTION_NPS,
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_RANKING,
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERIC_OPEN_ENDED,
  QUESTION_CONVERSATIONAL,
  GROUP_LOOP_MERGE,
} from 'data/nodeTypes';
import {
  PROPERTY_LOOP_MERGE,
  PROPERTY_SHUFFLE,
  PROPERTY_TYPE,
} from 'data/nodeProperties';
import {
  isGroup,
  isBasicGroup,
  isVanWestendorpGroup,
  isVanWestendorpQuestionType,
  isLoopAndMergeGroup,
} from 'utils/nodeTypes';
import { getVanWestendorpRecallAnswers } from 'containers/QuestionnairePage/GenericQuestion/VanWestendorpQuestion/helpers';
import {
  getAnswerDisplayLabel,
  getGroupPrefixedLabelFromMap,
} from 'containers/QuestionnairePage/selectorUtils';
import { SHUFFLE_NONE } from 'components/DropdownShuffle/constants';

import {
  MARK_TAGS,
  BLANK_PIPING_ANSWER,
  PIPE_EXPRESSION_MATCHING,
} from './constants';

const ELEMENT_TAGS = {
  DIV: () => ({ type: 'container' }),
  P: () => ({ type: 'paragraph' }),
};

const TEXT_TAGS = {
  STRONG: () => ({ bold: true }),
  EM: () => ({ italic: true }),
  U: () => ({ underline: true }),
  SPAN: (el) => ({ color: el?.style?.color }),
};

// export const pipingRegEx = /{{refId:([a-z0-9-]*)(\/subRefId:([a-z0-9-]*))?}}/;
export const pipingRegEx =
  /{{refId:([a-z0-9-]*)(\/subRefId:([a-z0-9-]*))?}}|{{attrRefId:([a-z0-9-]*)}}/;
export const pipingRegExAllWithoutVariables =
  /{{refId:([a-z0-9-]*)(\/subRefId:([a-z0-9-]*))?}}/g;
export const pipingRegExAll =
  /{{refId:([a-z0-9-]*)(\/subRefId:([a-z0-9-]*))?}}|{{attrRefId:([a-z0-9-]*)}}/g;
export const variableRegex = /{{attrRefId:([a-z0-9-]*)}}/;
export const variableRegexAll = /{{attrRefId:([a-z0-9-]*)}}/g;

export const hasPiping = (text = '') => new RegExp(pipingRegEx).test(text);

export const getReferencedSources = ({ text, flat }) => {
  const referencedSources = [];
  let match;

  do {
    match = pipingRegExAll.exec(text);
    if (match) {
      referencedSources.push(match);
    }
  } while (match);

  pipingRegExAll.lastIndex = 0;
  return flat
    ? referencedSources.map((question) => ({
        refId: question[PIPE_EXPRESSION_MATCHING.REF_ID],
        subRefId: question[PIPE_EXPRESSION_MATCHING.SUB_REF_ID],
        attrRefId: question[PIPE_EXPRESSION_MATCHING.ATTRIBUTE_REF_ID],
      }))
    : referencedSources;
};

export const wrapMentionsWithSpan = (htmlPrompt) => {
  const referencedQuestions = getReferencedSources({ text: htmlPrompt });

  if (referencedQuestions.length) {
    let parts = [];
    let pointerIndex = 0;

    referencedQuestions.forEach((referencedQuestion) => {
      const [mentionText] = referencedQuestion;
      const { index } = referencedQuestion;
      parts = [
        ...parts,
        htmlPrompt.slice(pointerIndex, index),
        `<span translate="no">${mentionText}</span>`,
      ];

      pointerIndex = index + mentionText.length;
    });

    return [...parts, htmlPrompt.slice(pointerIndex)].join('');
  }

  return htmlPrompt;
};

export const replaceRecallTagsInHtmlWithText = (html = '', text = '') =>
  String(html).replace(pipingRegExAll, text);

export const replaceRecallQuestionTagsInHtmlWithText = (html = '', text = '') =>
  String(html).replace(pipingRegExAllWithoutVariables, text);

export const replaceVariableTagsInHtmlWithText = (html = '', text = '') =>
  String(html).replace(variableRegexAll, text);

/**
 *
 * @param {String|Object|Array} item
 */
export const replaceMentionedPromptsWithBlank = (
  item,
  text = BLANK_PIPING_ANSWER,
) => {
  if (isString(item) && item.length) {
    return getPromptFromHtml(item.replace(pipingRegExAll, text));
  }
  if (isObject(item) && !isUndefined(item.prompt)) {
    const updates = {};
    updates.prompt = replaceMentionedPromptsWithBlank(item.prompt, text);

    if (item.questions) {
      updates.questions = item.questions.map((question) =>
        replaceMentionedPromptsWithBlank(question, text),
      );
    }

    if (item.answers) {
      updates.answers = item.answers.map((answer) => ({
        ...answer,
        body: replaceMentionedPromptsWithBlank(answer.body, text),
      }));
    }

    if (item.scorepoints) {
      updates.scorepoints = item.scorepoints.map((scorepoint) => ({
        ...scorepoint,
        body: replaceMentionedPromptsWithBlank(scorepoint.body, text),
      }));
    }
    if (item.matrix?.matrixAnswers) {
      updates.matrix = {
        ...item.matrix,
        matrixAnswers: item.matrix.matrixAnswers.map((answer) => ({
          ...answer,
          beforeLabel: replaceMentionedPromptsWithBlank(
            answer.beforeLabel,
            text,
          ),
          afterLabel: replaceMentionedPromptsWithBlank(answer.afterLabel, text),
        })),
      };

      if (item.matrix?.columns) {
        updates.matrix = {
          ...updates.matrix,
          columns: item.matrix.columns.map((column) => ({
            ...column,
            beforeLabel: replaceMentionedPromptsWithBlank(
              column.beforeLabel,
              text,
            ),
          })),
        };
      }

      if (item.matrix?.matrixExtraLabels) {
        updates.matrix = {
          ...updates.matrix,
          matrixExtraLabels: item.matrix.matrixExtraLabels.map(
            (extraLabel) => ({
              ...extraLabel,
              label: replaceMentionedPromptsWithBlank(extraLabel.label, text),
            }),
          ),
        };
      }
    }

    return {
      ...item,
      ...updates,
    };
  }

  if (isArray(item) && item.length) {
    return item.map((question) =>
      replaceMentionedPromptsWithBlank(question, text),
    );
  }

  return item;
};

export const getAttributeByRefIdFromGroupNode = (nodes, attributeRefId) =>
  Object.keys(nodes)
    .reduce((accumulator, refId) => {
      if (isLoopAndMergeGroup(nodes[refId].type)) {
        return [...accumulator, ...nodes[refId].attributes];
      }

      return accumulator;
    }, [])
    .find((attribute) => attribute.refId === attributeRefId);

// transforms prompt based on the refId of a previous question
// e.g "Hello {{refId:'questionRefId'}} {{refId:'questionRefId2'}} --> "Hello John Doe"
// also supports {{refId:'questionRefId'/subRefId:'answerRefId'}} for VanWestendorp questions
const deserializeMentionContent = (
  el,
  { questions, screeningQuestions, translations, readOnly },
) => {
  const { textContent } = el;
  const referencedSources = getReferencedSources({
    text: textContent,
  });
  const allQuestions = Object.assign({}, questions, screeningQuestions);

  let children = [];

  if (referencedSources.length) {
    let pointerIndex = 0;

    referencedSources.forEach((referencedQuestion) => {
      const [textToReplace, refId, , subRefId, attrRefId] = referencedQuestion;
      const { index } = referencedQuestion;
      const isTranslated = translations && !!translations[refId];
      const questionRefId = isTranslated ? translations[refId] : refId;
      const question = allQuestions[questionRefId];

      if (question) {
        const { type } = question;
        let { prompt } = question;
        // Prefix the label with the group name (e.g. "Group 1 Q3") when the
        // referenced source lives inside a group.
        const questionLabel = getGroupPrefixedLabelFromMap(
          question,
          allQuestions,
        );
        let label = questionLabel;
        if (translations && readOnly && !isTranslated) {
          prompt = question.originPrompt;
        }

        // handle vanWestendorp mention of specific answer
        if (subRefId) {
          switch (question.type) {
            case QUESTION_VAN_WESTENDORP: {
              const { cheap, expensive } = getVanWestendorpRecallAnswers(
                question,
                Boolean(readOnly && translations),
              );

              if (
                cheap.refId === subRefId ||
                cheap.translatedRefId === subRefId
              ) {
                ({ label, prompt } = cheap);
              } else if (
                expensive.refId === subRefId ||
                expensive.translatedRefId === subRefId
              ) {
                ({ label, prompt } = expensive);
              }
              break;
            }
            case QUESTION_MATRIX_SINGLE:
            case QUESTION_MATRIX_MULTIPLE:
            case QUESTION_MATRIX_BIPOLAR:
            case QUESTION_MATRIX_OPEN_ENDED:
            case QUESTION_MATRIX_NUMERIC_OPEN_ENDED: {
              ({ label, prompt } = getMentionLabelAndPrompt(
                {
                  ...question,
                  questionLabel,
                  subRefId,
                },
                Boolean(readOnly && translations),
              ));
              break;
            }
            default: {
              // keep the group-prefixed label, only resolve the prompt
              ({ prompt } = getMentionLabelAndPrompt(question));
              break;
            }
          }
        }

        const newPrompt = replaceMentionedPromptsWithBlank(prompt);

        children = [
          ...children,
          textContent.slice(pointerIndex, index),
          getNewMention({
            prompt: newPrompt,
            refId,
            type,
            label,
            subRefId,
          }),
        ];
      } else if (attrRefId) {
        const attribute = getAttributeByRefIdFromGroupNode(
          questions,
          attrRefId,
        );

        if (attribute) {
          children = [
            ...children,
            textContent.slice(pointerIndex, index),
            getNewMention(attribute),
          ];
        } else {
          children = [...children, textContent.slice(pointerIndex, index)];
        }
      } else {
        // if a question has been removed
        children = [...children, textContent.slice(pointerIndex, index)];
      }

      pointerIndex = index + textToReplace.length;
    });

    children.push(textContent.slice(pointerIndex));
  }

  if (children.length) {
    return children;
  }

  return textContent;
};

export const deserialize = (el, props) => {
  if (el.nodeType === 3) {
    return props?.questions
      ? deserializeMentionContent(el, props)
      : el.textContent;
  }
  if (el.nodeType !== 1) {
    return null;
  }

  const { nodeName } = el;
  const parent = el;
  const children = flatten(
    Array.from(parent.childNodes).map((item) => deserialize(item, props)),
  );

  if (el.nodeName === 'BODY') {
    return jsx('fragment', {}, children);
  }

  if (ELEMENT_TAGS[nodeName]) {
    const attrs = ELEMENT_TAGS[nodeName](el);
    const element = jsx('element', attrs, children);

    // prevent empty html prompt error
    if (element.children.length === 0) {
      return {
        ...element,
        children: [
          {
            text: '',
          },
        ],
      };
    }

    return element;
  }

  if (TEXT_TAGS[nodeName]) {
    const attrs = TEXT_TAGS[nodeName](el);
    return children.map((child) => {
      // QUICK FIX DUE TO CLASH OF MENTION WITH COLOR SPAN
      // RES-7030
      if (child.type === 'mention') {
        return child;
      }
      return jsx('text', attrs, child);
    });
  }

  return children;
};

export const deserializeHTML = (html = '', props) => {
  const parsed = new DOMParser().parseFromString(html, 'text/html');

  return deserialize(parsed.body, props);
};

export const getInnerContentFromHtmlString = (html = '') => {
  const htmlString = decorateHtmlIfNeeded(html);
  const parser = new DOMParser();
  const document = parser.parseFromString(htmlString, 'text/html');

  return document.body.firstChild.innerHTML;
};

const encodeHTMLtags = (html) => {
  let cleanedUp = replace(html, /</g, '&lt;');
  cleanedUp = replace(cleanedUp, />/g, '&gt;');

  return cleanedUp;
};

const getHtmlFromTextNode = (node) =>
  Object.entries(MARK_TAGS).reduce(
    (accumulator, [tag, mark]) => {
      if (!node[mark]) {
        return accumulator;
      }
      // TODO: support more than color styling in the future
      if (tag === 'color' && node[mark]) {
        return `<span style="color:${node[mark]}">${accumulator}</span>`;
      }
      return `<${tag}>${accumulator}</${tag}>`;
    },
    clearWhitespace(encodeHTMLtags(node.text)),
  );

const serialize = (node) => {
  const { type, children, refId, subRefId, loopAndMerge = false } = node;
  switch (type) {
    case 'container':
      return `<div>${serializeHTML(children)}</div>`;
    case 'paragraph':
      return `<p>${serializeHTML(children)}</p>`;
    case 'mention': {
      if (loopAndMerge) {
        return `{{attrRefId:${refId}}}`;
      }
      if (subRefId) {
        return `{{refId:${refId}/subRefId:${subRefId}}}`;
      }
      return `{{refId:${refId}}}`;
    }
    default:
      return getHtmlFromTextNode(node);
  }
};

export const serializeHTML = (values) => values.map(serialize).join('');
export const serializePlainText = (value) => {
  const val = value.map((node) => {
    if (Text.isText(node)) {
      return node.text;
    }
    const textValues = node.children.map((subnode) => {
      if (Text.isText(subnode)) {
        return subnode.text;
      }

      return serialize(subnode);
    });
    return textValues.join('');
  });
  return val.join('\n');
};

// TODO: revisit this
export const getPromptFromHtml = (
  htmlPrompt,
  questions,
  clearWhitespaces = true,
) => {
  const deserialized = deserializeHTML(htmlPrompt, questions);
  return clearWhitespaces
    ? clearWhitespace(serializePlainText(deserialized))
    : serializePlainText(deserialized);
};

export const withMentions = (editor) => {
  const { isInline, isVoid } = editor;

  // eslint-disable-next-line
  editor.isInline = (element) =>
    element.type === 'mention' ? true : isInline(element);
  // eslint-disable-next-line
  editor.isVoid = (element) =>
    element.type === 'mention' ? true : isVoid(element);

  return editor;
};

const getNewMention = (props) => {
  const { prompt, type, label, refId, subRefId } = props;
  const conditionalUpdates = {};

  if (props[PROPERTY_LOOP_MERGE]) {
    conditionalUpdates[PROPERTY_LOOP_MERGE] = props[PROPERTY_LOOP_MERGE];
  } else {
    conditionalUpdates.questionType = type;
    conditionalUpdates.subRefId = subRefId;
  }

  return {
    type: 'mention',
    character: prompt,
    children: [{ text: '' }],
    label,
    refId,
    ...conditionalUpdates,
  };
};

export const insertMention = (editor, props) => {
  const { prompt, refId, subRefId, translatedRefId, type, label } = props;

  Transforms.insertNodes(
    editor,
    getNewMention({
      prompt,
      refId: translatedRefId || refId,
      type,
      label,
      subRefId,
      [PROPERTY_LOOP_MERGE]: props[PROPERTY_LOOP_MERGE],
    }),
  );
  Transforms.move(editor);
};

export const insertAtChar = (editor) => {
  const node = { text: '@' };
  const { selection } = editor;

  if (
    !selection ||
    (Range.isCollapsed(selection) && !Editor.string(editor, selection).length)
  ) {
    node.purple = true;
  }

  Transforms.insertNodes(editor, [node]);
};

export const isTargetAMention = (editor, target) => {
  const { children } = editor;
  const {
    anchor: { path },
  } = target;
  const [containerIndex, childIndex] = path;
  const node = children[containerIndex].children[childIndex];

  return node && node.purple;
};

export const isMentioning = (editor) => {
  const { selection } = editor;

  if (selection && Range.isCollapsed(selection)) {
    const [start] = Range.edges(selection);
    const wordBefore = Editor.before(editor, start, { unit: 'word' });
    const before = wordBefore && Editor.before(editor, wordBefore);
    const beforeRange = before && Editor.range(editor, before, start);
    const beforeText = beforeRange && Editor.string(editor, beforeRange);
    const beforeMatch = beforeText && beforeText.match(/^@(\w+)$/);
    const after = Editor.after(editor, start);
    const afterRange = Editor.range(editor, start, after);
    const afterText = Editor.string(editor, afterRange);
    const afterMatch = afterText.match(/^(\s|$)/);

    if (beforeMatch && afterMatch) {
      return isTargetAMention(editor, beforeRange)
        ? [beforeRange, beforeMatch[1]]
        : false;
    }

    const charLocation = {
      ...selection,
      anchor: {
        ...selection.anchor,
        offset: selection.anchor.offset - 1,
      },
    };
    const charTyped = Editor.string(editor, charLocation);
    if (charTyped === '@') {
      return isTargetAMention(editor, charLocation)
        ? [charLocation, '']
        : false;
    }

    return false;
  }

  return false;
};

export const isMarkActive = (editor, types) => {
  const marks = Editor.marks(editor);
  return marks ? marks[types] === true : false;
};

export const toggleMark = (editor, type) => {
  const isActive = isMarkActive(editor, type);

  if (isActive) {
    Editor.removeMark(editor, type);
  } else {
    Editor.addMark(editor, type, true);
  }
};

/*!
 * Check if an element is out of the viewport
 * (c) 2018 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param  {Node}  elem The element to check
 * @return {Object}     A set of booleans for each side of the element
 */
const isOutOfViewport = ({ elem, rect }) => {
  // element's bounding
  const bounding = rect || elem.getBoundingClientRect();

  // Check if it's out of the viewport on each side
  const out = {};
  out.top = bounding.top < 0;
  out.left = bounding.left < 0;
  out.bottom =
    bounding.bottom >
    (window.innerHeight || document.documentElement.clientHeight);
  out.right =
    bounding.right >
    (window.innerWidth || document.documentElement.clientWidth);
  out.any = out.top || out.left || out.bottom || out.right;
  out.all = out.top && out.left && out.bottom && out.right;

  return out;
};

export const getRectPosition = (rect) => {
  const out = isOutOfViewport({ rect });
  let top = rect.top + window.pageYOffset;
  let left = rect.left + window.pageXOffset;

  if (out.right) {
    left -= rect.width;
  }

  if (out.bottom) {
    top = top - rect.height - 34;
  }

  return {
    ...rect,
    top: top || rect.top,
    left: left || rect.left,
  };
};

export const canQuestionBeMentioned = ({
  fromQuestion = {},
  toQuestion = {},
  allQuestions = {},
  hasExtraChecks,
}) => {
  const { groupRefId: fromGroupRefId, type: fromType } = fromQuestion;
  const { groupRefId: toGroupRefId, type: toType } = toQuestion;

  if (hasExtraChecks) {
    const fromTypeAlwaysNoMentions = [
      QUESTION_DESCRIPTION,
      QUESTION_RATING,
      QUESTION_SLIDER,
      QUESTION_RANKING,
      QUESTION_NPS,
      QUESTION_MATRIX_SINGLE,
      QUESTION_MATRIX_MULTIPLE,
      QUESTION_MATRIX_BIPOLAR,
      QUESTION_MATRIX_OPEN_ENDED,
      QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
      QUESTION_MATRIX_CONSTANT_SUM,
      QUESTION_CONVERSATIONAL,
    ].includes(fromType);

    const toTypeAlwaysNoMentions = [
      QUESTION_OPEN_ENDED,
      QUESTION_NUMERIC_OPEN_ENDED,
      QUESTION_DESCRIPTION,
      QUESTION_RATING,
      QUESTION_SLIDER,
      QUESTION_DRILLDOWN,
      QUESTION_NPS,
      QUESTION_CONVERSATIONAL,
    ].includes(toType);

    const toTypeFromTypeComboNoMentions =
      [
        QUESTION_SINGLE,
        QUESTION_MULTIPLE,
        QUESTION_RANKING,
        QUESTION_MATRIX_SINGLE,
        QUESTION_MATRIX_MULTIPLE,
        QUESTION_MATRIX_BIPOLAR,
        QUESTION_MATRIX_OPEN_ENDED,
        QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
        QUESTION_MATRIX_CONSTANT_SUM,
        QUESTION_CONVERSATIONAL,
      ].includes(toType) &&
      [
        QUESTION_DESCRIPTION,
        QUESTION_RATING,
        QUESTION_SLIDER,
        QUESTION_RANKING,
        QUESTION_NPS,
        QUESTION_MATRIX_SINGLE,
        QUESTION_MATRIX_MULTIPLE,
        QUESTION_MATRIX_BIPOLAR,
        QUESTION_MATRIX_OPEN_ENDED,
        QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
        QUESTION_MATRIX_CONSTANT_SUM,
        QUESTION_CONVERSATIONAL,
      ].includes(fromType);

    if (
      fromTypeAlwaysNoMentions ||
      toTypeAlwaysNoMentions ||
      toTypeFromTypeComboNoMentions ||
      isGroup(fromType)
    )
      return false;
  }

  if (
    [
      QUESTION_DESCRIPTION,
      QUESTION_MATRIX_OPEN_ENDED,
      QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
      QUESTION_MATRIX_CONSTANT_SUM,
      QUESTION_CONVERSATIONAL,
    ].includes(fromType) ||
    isGroup(fromType)
  )
    return false;

  const fromGroupQuestion = allQuestions[fromGroupRefId];
  const fromGroupType = get(fromGroupQuestion, PROPERTY_TYPE);
  const fromGroupRandomization = get(fromGroupQuestion, PROPERTY_SHUFFLE);
  const isFromGroupRandomized =
    !isNil(fromGroupRandomization) && fromGroupRandomization !== SHUFFLE_NONE;

  const toGroupQuestion = allQuestions[toGroupRefId];
  const toGroupType = get(toGroupQuestion, PROPERTY_TYPE);

  // to VW , only allow from same VW
  if (toGroupType && isVanWestendorpGroup(toGroupType)) {
    // from VW group
    if (fromGroupType && isVanWestendorpGroup(fromGroupType)) {
      if (
        fromGroupRefId === toGroupRefId &&
        isVanWestendorpQuestionType(fromType)
      ) {
        return true;
      }
    }
    return false;
  }

  // from question is not in group
  if (!fromGroupType) {
    return true;
  }

  // from question is in basic group (non-randomized)
  if (isBasicGroup(fromGroupType) && !isFromGroupRandomized) {
    return true;
  }

  if (isLoopAndMergeGroup(fromGroupType)) {
    return false;
  }

  // from question is in basic group (randomizd)
  if (isBasicGroup(fromGroupType) && isFromGroupRandomized) {
    // do not allow in same group
    if (fromGroupRefId === toGroupRefId) {
      return false;
    }
    return true;
  }

  return false;
};

export const canQuestionHaveMention = ({
  question,
  nodes,
  readOnly,
  translations,
  randomizeQuestionnaireEnabled,
  isQuestionPrompt,
}) => {
  const loopAndMergeMentionsEnabled = Boolean(
    question?.groupRefId &&
    nodes?.[question?.groupRefId]?.type === GROUP_LOOP_MERGE,
  );

  if (!question || (randomizeQuestionnaireEnabled && !question.isScreening)) {
    return {
      mentionsEnabled: false,
      loopAndMergeMentionsEnabled,
    };
  }

  let mentionsEnabled = !readOnly && !!nodes;

  // disable piping on translated questions that don't have piping on original.
  if (translations && question && isQuestionPrompt && !question.hasPiping) {
    mentionsEnabled = false;
  }

  return {
    mentionsEnabled,
    loopAndMergeMentionsEnabled,
  };
};

export function decorateHtmlIfNeeded(text) {
  if (!startsWith(text, '<div>')) {
    return `<div>${text || ''}</div>`;
  }

  return text;
}

export function getMentionLabelAndPrompt(searchItemToMention, usePlaceholder) {
  if (searchItemToMention.subRefId) {
    const matrixAnswers = get(searchItemToMention, 'matrix.matrixAnswers');
    const transformedAnswers = searchItemToMention.answers.map((answer) => {
      let answerBody = usePlaceholder ? answer.placeholder : answer.body;

      if (matrixAnswers) {
        const matrixAnswer = matrixAnswers.find(
          ({ answerRefId }) => answerRefId === answer.refId,
        );
        answerBody = usePlaceholder
          ? matrixAnswer.beforePlaceholder
          : matrixAnswer.beforeLabel;

        if (matrixAnswer.afterLabel) {
          answerBody += ` | ${
            usePlaceholder
              ? matrixAnswer.afterPlaceholder
              : matrixAnswer.afterLabel
          }`;
        }
      } else if (answer.matrixAnswer) {
        const { matrixAnswer } = answer;
        answerBody = usePlaceholder
          ? matrixAnswer.beforePlaceholder
          : matrixAnswer.beforeLabel;
        if (matrixAnswer.afterLabel) {
          answerBody += ` | ${
            usePlaceholder
              ? matrixAnswer.afterPlaceholder
              : matrixAnswer.afterLabel
          }`;
        }
      }

      return replaceMentionedPromptsWithBlank({
        refId: answer.refId,
        translatedRefId: answer.translatedRefId,
        label: `${searchItemToMention.questionLabel}:`,
        prompt: `${getAnswerDisplayLabel(answer)} - ${answerBody}`,
      });
    });
    const { label, prompt } =
      transformedAnswers.find(
        ({ refId, translatedRefId }) =>
          refId === searchItemToMention.subRefId ||
          translatedRefId === searchItemToMention.subRefId,
      ) || {};

    return {
      ...searchItemToMention,
      label,
      prompt,
    };
  }

  return searchItemToMention;
}

export const selectCompanyText = (editor) => {
  const prompt = Editor.string(editor, []);
  const searchText = '[ company ]';
  const index = prompt.indexOf(searchText);
  const offset = searchText.length;

  if (index !== -1) {
    Transforms.select(editor, {
      anchor: { path: [0, 0], offset: index },
      focus: { path: [0, 0], offset: index + offset },
    });
  }
};
